import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { ExpandableInfo, ServiceModelDetails, ReadMoreText } from 'containers'
import { Button, InputText, Heading, ErrorIcon, TickModalIcon, LoaderIcon } from 'components'
import { DEFAULT_THRESHOLD_NORMAL } from 'constants/layout'
import { IS_BROWSER, WINDOW } from 'constants/environment'
import { withRadium, DetectCompact, gtmEvent } from 'higher-order-components'
import { history } from 'utils/history'
import { gtmRecallsActionTypes } from 'store/gtm-events'

// Move this to a separate component along with one in calculator
const VinLocator = () => {
  const descriptionText = `
      The Vehicle Identification Number [VIN] is a unique
      number that identifies your vehicle. Please refer
      to the Specifications section of your owner's manual
      for the exact location of this number.
    `
  const mainText = 'Where is my VIN located?'
  return (
    <ExpandableInfo
      mainText={mainText}
      description={descriptionText}
      isOpen
      descriptionStyle={{ textAlign: 'center' }}
    />
  )
}

class RecallsBlock extends Component {
  constructor(props) {
    super(props)
    this.state = {
      displaySpinner: false,
      vinOrRegoInput: '',
      isUpdatingVINRego: false,
      recallData: null,
      mode: 'input' // input, recall or norecall
    }
  }

  componentDidMount() {
    if (!IS_BROWSER) return
    const search = new window.URL(window.location).searchParams
    const vin = this.props.vin || search.get('VIN')

    if (vin) {
      this.setState(
        {
          vinOrRegoInput: vin
        },
        () => {
          this.fetchRecallsForVinOrRego()
        }
      )
    }

    this.unlistenHistory = history.listen((location, action) => {
      const searchParams = new window.URL(window.location).searchParams
      const vin = this.props.vin || searchParams.get('VIN')
      const oldVin = this.state.recallData ? this.state.recallData.recallResponse.vin : null

      this.setState({ vinOrRegoInput: vin }, () => {
        if (oldVin !== vin) this.fetchRecallsForVinOrRego()
        else this.syncUrl()
      })
    })
  }

  handleNext = () => {
    this.setState({ isUpdatingVINRego: false })
    this.fetchRecallsForVinOrRego()
  }

  setVinOrRego = e => {
    this.setState({ vinOrRegoInput: e.target.value.trim(), errorMessage: '' })
  }

  handleChangeVinOrRego = () => {
    this.setState(
      {
        displaySpinner: false,
        recallData: null,
        vinOrRegoInput: '',
        isUpdatingVINRego: true,
        mode: 'input'
      },
      () => {
        history.push({
          pathname: WINDOW.location.href.split('?')[0]
        })
      }
    )
  }

  fetchRecallsForVinOrRego = () => {
    if (this.state.vinOrRegoInput && this.state.vinOrRegoInput !== '') {
      const url = this.props.endpoint
      this.setState({
        errorMessage: '',
        displaySpinner: true,
        isPopupOpen: false
      })
      const options = {
        method: 'POST',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          vin: this.state.vinOrRegoInput
        })
      }

      /** @todo convert this to use apis/koala/fetchKoalaData * */
      fetch(url, options)
        .then(response => {
          if (response.status) return response.json()

          throw new Error(
            `Unfortunately the service is unavailable. Please try again later or contact your Mazda Dealer (Status ${
              response.statusCode
            }).`
          )
        })
        .then(json => {
          const { message } = json
          /** @todo Add schema validation for API call */
          const isValid = true
          /** @todo Handle this in validaterecallData if possible */
          if (!isValid) {
            this.setState({
              displaySpinner: false,
              errorMessage:
                'Unfortunately data received from the server is invalid. Please try again later or contact your Mazda Dealer'
            })
            return
          }

          if (isValid && message) {
            this.setState({
              displaySpinner: false,
              errorMessage: message
            })
            return
          }

          this.setState(
            {
              displaySpinner: false,
              errorMessage: message,
              recallData: message ? null : json,
              mode:
                json.recallResponse.recallActivities &&
                json.recallResponse.recallActivities.length > 0
                  ? 'recall'
                  : 'norecall'
            },
            () => {
              !this.props.isMyMazdaBlock && this.syncUrl()
              // If VIN submitted successfully, fire GTM event
              this.props.gtmEvent({
                gtmID: gtmRecallsActionTypes.CHECK_VIN,
                gtmPayload: {
                  result:
                    json.recallResponse.recallActivities.length > 0
                      ? this.renderRecallMessage()
                      : this.props.noRecallMessage || 'NO OUTSTANDING RECALLS'
                }
              })
            }
          )
        })
        .catch(error => {
          this.setState({
            displaySpinner: false,
            errorMessage: error.message
          })
          console.warn(`URL: ${url}`, error)
        })
    }
  }

  syncUrl = () => {
    if (!this.state.recallData || !IS_BROWSER) return

    if (this.state.recallData.recallResponse) {
      const vin = this.state.recallData.recallResponse.vin

      const url = new window.URL(window.location)

      url.searchParams.set('VIN', vin)

      if (window.location.pathname !== url.pathname || window.location.search !== url.search) {
        history.push({
          pathname: url.pathname,
          search: url.search
        })
      }
    }
  }

  renderCheckAnotherVehicleButton = () => {
    const buttonLabel = 'CHECK ANOTHER VEHICLE'
    return (
      <Button
        label={buttonLabel}
        styleName="blackOutlined"
        onClick={this.handleChangeVinOrRego}
        customWrapperStyle={{ margin: '.3em' }}
        fullWidthMobile
        gtmEvents={[
          {
            func: this.props.gtmEvent,
            args: [
              {
                gtmID: gtmRecallsActionTypes.CLICK_CTA,
                gtmPayload: {
                  ctaName: buttonLabel,
                  result:
                    this.state.recallData.recallResponse.recallActivities.length > 0
                      ? this.renderRecallMessage()
                      : this.props.noRecallMessage || 'NO OUTSTANDING RECALLS'
                }
              }
            ]
          }
        ]}
      />
    )
  }

  renderRecallDetails = () => {
    const { recallResponse } = this.state.recallData
    return recallResponse.recallActivities.map((recall, index) => (
      <div style={[styles.border, { textAlign: 'left' }]}>
        <Heading
          customHeadingStyle={{ margin: '3.5em 0 1em' }}
          tag="h5"
          value={
            recallResponse.recallActivities.length > 1
              ? `${index + 1}. ${recall.description}`
              : recall.description
          }
        />
        <div>
          Recall Code: <span style={{ fontWeight: 200 }}>{recall.serviceActivityCode}</span>
        </div>
        {recall.summaryText && (
          <div>
            <Heading customHeadingStyle={styles.heading} tag="h5" value={'SUMMARY'} />
            <div style={styles.text}>{recall.summaryText}</div>
          </div>
        )}
        <ReadMoreText isCompactOnly compact={this.props.compact}>
          {recall.riskText && (
            <div>
              <Heading customHeadingStyle={styles.heading} tag="h5" value={'RISK'} />
              <div style={styles.text}>{recall.riskText}</div>
            </div>
          )}
          {recall.remedyText && (
            <div>
              <Heading customHeadingStyle={styles.heading} tag="h5" value={'REMEDY'} />
              <div style={styles.text}>{recall.remedyText}</div>
            </div>
          )}
        </ReadMoreText>
      </div>
    ))
  }

  renderRecallMessage = () => {
    const { recallData } = this.state
    const { recallMessage } = this.props
    if (recallMessage && recallMessage.includes('{number}')) {
      return recallMessage.replace('{number}', recallData.recallResponse.recallActivities.length)
    }
    return `YOUR VEHICLE IS AFFECTED BY ${
      recallData.recallResponse.recallActivities.length
    } RECALL${recallData.recallResponse.recallActivities.length > 1 ? 'S' : ''}`
  }

  renderModelDetails = recallData => (
    <ServiceModelDetails
      model={recallData.recallResponse.modelDescription}
      vin={recallData.recallResponse.vin}
      compact={this.props.compact}
    />
  )

  render() {
    const { displaySpinner, vinOrRegoInput, recallData, errorMessage, mode } = this.state
    const {
      heading,
      noRecallMessage,
      recallMessageDescription,
      noRecallMessageDescription,
      findADealerCta,
      isMyMazdaBlock = false,
      buttonText,
      buttonUrl
    } = this.props
    const renderInput = true
    const findADealerCtaName = (findADealerCta && findADealerCta.props.label) || 'Find A Dealer'
    if (mode === 'recall') {
      return (
        <div style={styles.background}>
          <div style={styles.container}>
            {!isMyMazdaBlock && (
              <Heading
                customHeadingStyle={{ marginBottom: '2em' }}
                tag="h3"
                value={heading || 'HAS MY MAZDA BEEN RECALLED?'}
              />
            )}
            <ErrorIcon
              style={{ width: '2em', height: '2em', marginBottom: '1em', fill: '#910A2D' }}
            />
            <Heading
              tag="h3"
              customHeadingStyle={{ color: '#910A2D' }}
              value={this.renderRecallMessage()}
            />
            {this.renderModelDetails(recallData)}
            <div style={[styles.border, styles.descriptionText]}>
              {recallMessageDescription ||
                'Your vehicle is affected by the following outstanding recalls. Please continue through to the next step so that Mazda can facilitate the booking of your vehicle for repair.'}
            </div>
            {this.renderRecallDetails()}
            {!isMyMazdaBlock && (
              <div style={styles.buttonGroup}>
                <Button
                  label={findADealerCtaName}
                  href={findADealerCta.props.href || '/find-a-dealer'}
                  customWrapperStyle={{ margin: '.3em' }}
                  styleName={findADealerCta.props.styleName || 'red'}
                  external={findADealerCta.props.external}
                  gtmEvents={[
                    {
                      func: this.props.gtmEvent,
                      args: [
                        {
                          gtmID: gtmRecallsActionTypes.CLICK_CTA,
                          gtmPayload: {
                            ctaName: findADealerCtaName,
                            result: this.renderRecallMessage()
                          }
                        }
                      ]
                    }
                  ]}
                />
                {this.renderCheckAnotherVehicleButton()}
              </div>
            )}
            {isMyMazdaBlock && (
              <div style={styles.buttonGroup}>
                <Button
                  label={buttonText}
                  href={buttonUrl}
                  customWrapperStyle={{ margin: '.3em' }}
                  styleName="red"
                  gtmEvents={[
                    {
                      func: this.props.gtmEvent,
                      args: [
                        {
                          gtmID: gtmRecallsActionTypes.CLICK_CTA,
                          gtmPayload: {
                            ctaName: buttonText,
                            result: this.renderRecallMessage()
                          }
                        }
                      ]
                    }
                  ]}
                />
              </div>
            )}
          </div>
        </div>
      )
    } else if (mode === 'norecall') {
      return (
        <div style={styles.background}>
          <div style={styles.container}>
            <Heading
              customHeadingStyle={{ marginBottom: '2em' }}
              tag="h3"
              value={heading || 'HAS MY MAZDA BEEN RECALLED?'}
            />
            <TickModalIcon style={{ width: '2em', height: '2em', marginBottom: '1em' }} />
            <Heading
              tag="h3"
              customHeadingStyle={{ color: '#7DBE37' }}
              value={noRecallMessage || 'NO OUTSTANDING RECALLS'}
            />
            {this.renderModelDetails(recallData)}
            {!isMyMazdaBlock && this.renderCheckAnotherVehicleButton()}
          </div>
        </div>
      )
    }
    return (
      <div
        style={{
          backgroundColor: '#fff',
          padding: '2em 0'
        }}>
        <div style={styles.container}>
          <Heading
            customHeadingStyle={styles.heading}
            tag="h3"
            value={heading || 'HAS MY MAZDA BEEN RECALLED?'}
          />
          <div style={styles.regoInput}>
            {renderInput && (
              <div style={{ flex: '0 1 100%', marginRight: '2em' }}>
                <InputText
                  onKeyDown={e => {
                    e.key === 'Enter' && this.handleNext()
                  }}
                  placeholder="Enter your VIN here"
                  value={vinOrRegoInput}
                  onChange={this.setVinOrRego}
                />
              </div>
            )}
            {renderInput && (
              <div style={{ flex: '0 3 100%' }}>
                <Button
                  styleName="black"
                  label={'check'}
                  onClick={this.handleNext}
                  display={'block'}
                  disabled={!vinOrRegoInput || displaySpinner}
                  fullWidth
                />
              </div>
            )}
          </div>
          {renderInput && <VinLocator />}
          {displaySpinner && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                margin: '1em'
              }}>
              <LoaderIcon style={{ margin: '5em' }} styleName="light" />
            </div>
          )}
          {errorMessage && <div style={styles.error}>{errorMessage}</div>}
        </div>
      </div>
    )
  }
}

RecallsBlock.propTypes = {
  endpoint: PropTypes.string,
  compact: PropTypes.bool,
  vin: PropTypes.string,
  heading: PropTypes.string,
  recallMessage: PropTypes.string,
  noRecallMessage: PropTypes.string,
  recallMessageDescription: PropTypes.string,
  noRecallMessageDescription: PropTypes.string,
  findADealerCta: PropTypes.object,
  gtmEvent: PropTypes.func
}

export default withRadium(DetectCompact(), gtmEvent())(RecallsBlock)

const breakpoint1 = `@media (min-width: ${DEFAULT_THRESHOLD_NORMAL}px)`

const styles = {
  background: {
    backgroundColor: '#fff',
    padding: '5em 0'
  },
  container: {
    margin: '0 auto',
    maxWidth: '50em',
    textAlign: 'center',
    padding: '0 1.25em'
  },
  text: {
    fontWeight: 200,
    lineHeight: '27px'
  },
  heading: {
    margin: '2.5em 0 1em'
  },
  border: {
    paddingBottom: '3em',
    borderBottom: '1px solid #eee'
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginTop: '2.5em',
    [breakpoint1]: {
      marginTop: '3.5em',
      flexDirection: 'row'
    }
  },
  error: {
    textAlign: 'center',
    fontWeight: 200,
    color: '#910A2D',
    padding: '1em',
    margin: '1em auto',
    lineHeight: 1.8
  },
  regoInput: {
    width: '100%',
    margin: 'auto',
    display: 'flex',
    flexDirection: 'column',
    [breakpoint1]: {
      flexDirection: 'row',
      maxWidth: '30em',
      alignItems: 'center'
    }
  },
  descriptionText: {
    fontWeight: 200,
    marginTop: '1em',
    paddingBottom: '3em'
  }
}



// WEBPACK FOOTER //
// ./src/containers/recalls-block/index.jsx